import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Clipboard, Image, Linking, View } from 'react-native';

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { showToast } from '@/store/toastStore';
import { getPaymentErrorMessage } from '@/utils/api-error';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { elementsAppearance } from './elements-appearance';
import { useCheckoutPaymentIntent } from '@/hooks/use-checkout-payment-intent';
import { usePublicPaymentPricing } from '@/hooks/use-public-payment-pricing';
import {
  CHECKOUT_BOLETO,
  CHECKOUT_EBANX_NOTICE,
  CHECKOUT_PIX,
  getCheckoutCardBrandsNotice,
} from '@/content/payments/checkout';
import type { AutomaticCheckoutProps } from '@/types/checkout';
import { PAYMENT_METHOD } from '@/types/payment-method';
import {
  DEV_TEST_PAYMENT_METHOD,
  DevTestCardButton,
  isDevTestCardEnabled,
} from '@/components/checkout/dev-test-card-button';

/**
 * Checkout automático (T5.1, plano 6.7), variante **web**: `Elements` +
 * `PaymentElement` clássicos (`@stripe/react-stripe-js`, sem o subpath
 * `/checkout` — aquele é para Checkout Session, e aqui o `clientSecret` é de
 * um `PaymentIntent`, T2.2). Padrão visual de `app/subscription/checkout.tsx`
 * (mesma `appearance`), mas fluxo de confirmação próprio: Pix e boleto
 * revelam QR/linha digitável **na página**, sem redirecionar (plano 6.7).
 *
 * Par nativo: `automatic-checkout.tsx` (`PaymentSheet` do
 * `@stripe/stripe-react-native` — o Payment Element não existe fora da web).
 */
export function AutomaticCheckout({ chargeId }: AutomaticCheckoutProps) {
  const { state, regenerate } = useCheckoutPaymentIntent(chargeId, true);
  const { data: pricing } = usePublicPaymentPricing();
  const colorScheme = useColorScheme();

  const readyData = state.status === 'ready' ? state.data : null;
  const stripePromise = useMemo(() => {
    if (!readyData) return null;
    return loadStripe(readyData.publishableKey, { stripeAccount: readyData.stripeAccount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyData?.publishableKey, readyData?.stripeAccount]);

  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <View className="items-center py-8">
        <LoadingSpinner message="Preparando pagamento..." />
      </View>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <View className="gap-2 rounded-lg border border-border p-4">
        <ThemedText className="text-sm text-foreground/70">{state.message}</ThemedText>
      </View>
    );
  }

  if (state.status === 'error') {
    return (
      <ErrorState
        variant="compact"
        title="Não foi possível preparar o pagamento"
        message={state.message}
        onRetry={regenerate}
      />
    );
  }

  if (!readyData || !stripePromise) return null;

  const showCardBrandsNotice =
    readyData.paymentMethods.includes(PAYMENT_METHOD.CREDIT_CARD) && pricing;
  const showBoletoNotice = readyData.paymentMethods.includes(PAYMENT_METHOD.BANK_SLIP);

  return (
    <View className="gap-4">
      {showCardBrandsNotice && (
        <View className="flex-row items-start gap-2">
          <Ionicons
            className="text-neutral-500"
            name="card-outline"
            size={16}
            style={{ marginTop: 1 }}
          />
          <ThemedText className="flex-1 text-xs text-foreground/60">
            {getCheckoutCardBrandsNotice(pricing!)}
          </ThemedText>
        </View>
      )}

      <View className="flex-row items-start gap-2">
        <Ionicons
          className="text-neutral-500"
          name="information-circle-outline"
          size={16}
          style={{ marginTop: 1 }}
        />
        <ThemedText className="flex-1 text-xs text-foreground/60">
          {CHECKOUT_EBANX_NOTICE}
        </ThemedText>
      </View>

      <Elements
        key={readyData.clientSecret}
        stripe={stripePromise}
        options={{
          clientSecret: readyData.clientSecret,
          appearance: elementsAppearance(colorScheme),
        }}
      >
        <PaymentElementForm
          onRegenerate={regenerate}
          clientSecret={readyData.clientSecret}
          devTestCard={
            isDevTestCardEnabled(readyData.publishableKey) &&
            readyData.paymentMethods.includes(PAYMENT_METHOD.CREDIT_CARD)
          }
        />
      </Elements>

      {showBoletoNotice && (
        <ThemedText className="text-xs text-foreground/50">
          {CHECKOUT_BOLETO.requiresDocumentNotice} {CHECKOUT_BOLETO.compensationNotice}
        </ThemedText>
      )}
    </View>
  );
}

// -- Pix/boleto: campos que o `next_action` traz para conta BR, mas que o
// `@stripe/stripe-js` ainda não tipa (só `wechat_pay_display_qr_code` está
// no d.ts hoje). Formato conferido na documentação pública do Stripe
// ("Aceitar um pagamento Pix"/"Boleto", integração com PaymentIntent) — não
// foi possível conferir contra uma resposta real: Pix está bloqueado para
// toda a plataforma (T0.2, Registro) e nenhuma subconta de teste chegou a
// `charges_enabled`. Testar assim que uma conta destravar.
interface PixDisplayQrCodeNextAction {
  data: string;
  image_url_png?: string;
  hosted_instructions_url?: string;
  expires_at: number;
}
interface BoletoDisplayDetailsNextAction {
  expires_at: number | null;
  hosted_voucher_url: string | null;
  number: string | null;
  pdf: string | null;
}
type BrNextAction = PaymentIntent.NextAction & {
  pix_display_qr_code?: PixDisplayQrCodeNextAction;
  boleto_display_details?: BoletoDisplayDetailsNextAction;
};

type RevealState =
  | { kind: 'form' }
  | { kind: 'pix'; data: string; imageUrl?: string; expiresAt: number }
  | { kind: 'pix-expired' }
  | { kind: 'boleto'; number: string | null; voucherUrl: string | null }
  | { kind: 'waiting' };

function PaymentElementForm({
  onRegenerate,
  clientSecret,
  devTestCard,
}: {
  onRegenerate: () => void;
  clientSecret: string;
  /** Atalho de dev: confirma com o cartão de teste, sem preencher o iframe. */
  devTestCard: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reveal, setReveal] = useState<RevealState>({ kind: 'form' });

  // QR do Pix vencido (plano 6.7): troca a tela sozinha quando o prazo passa,
  // sem repique — um único `setTimeout` disparando no instante certo (não um
  // `setInterval` checando cor/estilo).
  useEffect(() => {
    if (reveal.kind !== 'pix') return;
    const msLeft = reveal.expiresAt * 1000 - Date.now();
    if (msLeft <= 0) {
      setReveal({ kind: 'pix-expired' });
      return;
    }
    const timeout = setTimeout(() => setReveal({ kind: 'pix-expired' }), msLeft);
    return () => clearTimeout(timeout);
  }, [reveal]);

  const handleCopy = useCallback(async (text: string) => {
    await Clipboard.setString(text);
    showToast('Código copiado!', 'success');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: typeof window !== 'undefined' ? window.location.href : '',
      },
    });

    setSubmitting(false);

    if (error) {
      setErrorMessage(getPaymentErrorMessage(error, 'Não foi possível confirmar o pagamento.'));
      return;
    }

    const nextAction = paymentIntent?.next_action as BrNextAction | null | undefined;

    if (nextAction?.type === 'pix_display_qr_code' && nextAction.pix_display_qr_code) {
      const pix = nextAction.pix_display_qr_code;
      setReveal({
        kind: 'pix',
        data: pix.data,
        imageUrl: pix.image_url_png,
        expiresAt: pix.expires_at,
      });
      return;
    }

    if (nextAction?.type === 'boleto_display_details' && nextAction.boleto_display_details) {
      const boleto = nextAction.boleto_display_details;
      setReveal({ kind: 'boleto', number: boleto.number, voucherUrl: boleto.hosted_voucher_url });
      return;
    }

    // Cartão (ou já `succeeded`): não marca como pago aqui — quem confirma é
    // o webhook (T2.3); o polling da tela de checkout vira "Pago" sozinho.
    setReveal({ kind: 'waiting' });
  }, [stripe, elements]);

  const handleDevTestCard = useCallback(async () => {
    if (!stripe) return;
    setSubmitting(true);
    setErrorMessage(null);
    const { error } = await stripe.confirmPayment({
      clientSecret,
      redirect: 'if_required',
      confirmParams: {
        payment_method: DEV_TEST_PAYMENT_METHOD,
        return_url: typeof window !== 'undefined' ? window.location.href : '',
      },
    });
    setSubmitting(false);
    if (error) {
      setErrorMessage(getPaymentErrorMessage(error, 'Não foi possível confirmar o pagamento.'));
      return;
    }
    setReveal({ kind: 'waiting' });
  }, [stripe, clientSecret]);

  if (reveal.kind === 'pix') {
    return (
      <View className="gap-3 rounded-lg border border-border p-4">
        <ThemedText className="text-center font-semibold text-sm">
          {CHECKOUT_PIX.waitingTitle}
        </ThemedText>
        {reveal.imageUrl && (
          <View className="items-center">
            <Image
              source={{ uri: reveal.imageUrl }}
              accessibilityLabel="QR code Pix para pagar"
              style={{ width: 200, height: 200 }}
              resizeMode="contain"
            />
          </View>
        )}
        <View className="rounded-lg bg-muted p-3">
          <ThemedText className="font-mono text-xs" numberOfLines={3}>
            {reveal.data}
          </ThemedText>
        </View>
        <Button variant="outline" onPress={() => handleCopy(reveal.data)}>
          <View className="flex-row items-center gap-2">
            <Ionicons className="text-foreground" name="copy" size={18} />
            <ThemedText>Copiar código Pix</ThemedText>
          </View>
        </Button>
      </View>
    );
  }

  if (reveal.kind === 'pix-expired') {
    return (
      <View className="gap-3 rounded-lg border border-border p-4">
        <ThemedText className="text-center text-sm text-foreground/70">
          O código Pix expirou.
        </ThemedText>
        <Button variant="default" onPress={onRegenerate}>
          <ThemedText className="font-semibold text-white">{CHECKOUT_PIX.expiredButton}</ThemedText>
        </Button>
      </View>
    );
  }

  if (reveal.kind === 'boleto') {
    return (
      <View className="gap-3 rounded-lg border border-border p-4">
        <ThemedText className="text-center text-sm text-foreground/70">
          {CHECKOUT_BOLETO.compensationNotice}
        </ThemedText>
        {reveal.number && (
          <View className="rounded-lg bg-muted p-3">
            <ThemedText className="font-mono text-xs">{reveal.number}</ThemedText>
          </View>
        )}
        <View className="gap-2">
          {reveal.number && (
            <Button variant="outline" onPress={() => handleCopy(reveal.number!)}>
              <View className="flex-row items-center gap-2">
                <Ionicons className="text-foreground" name="copy" size={18} />
                <ThemedText>Copiar linha digitável</ThemedText>
              </View>
            </Button>
          )}
          {reveal.voucherUrl && (
            <Button variant="outline" onPress={() => Linking.openURL(reveal.voucherUrl!)}>
              <View className="flex-row items-center gap-2">
                <Ionicons className="text-foreground" name="document-text-outline" size={18} />
                <ThemedText>Ver boleto</ThemedText>
              </View>
            </Button>
          )}
        </View>
      </View>
    );
  }

  if (reveal.kind === 'waiting') {
    return (
      <View className="items-center gap-2 rounded-lg border border-border p-4">
        <ActivityIndicator size="small" />
        <ThemedText className="text-center text-sm text-foreground/70">
          Aguardando confirmação do pagamento...
        </ThemedText>
      </View>
    );
  }

  return (
    <View className="gap-4">
      {devTestCard && <DevTestCardButton onPress={handleDevTestCard} busy={submitting} />}
      <View className="w-full overflow-hidden" style={{ minHeight: 200 }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </View>
      {errorMessage && (
        <View className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <ThemedText className="text-sm text-red-600">{errorMessage}</ThemedText>
        </View>
      )}
      <Button onPress={handleSubmit} disabled={submitting || !stripe}>
        {submitting ? (
          <ActivityIndicator size="small" color="white" />
        ) : (
          <ThemedText className="font-semibold text-white">Pagar</ThemedText>
        )}
      </Button>
    </View>
  );
}
